import { Heading } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { LivechatMessageModel } from '@mm/state/model/LivechatMessageModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useActor } from '@xstate/react';
import conversation from 'assets/conversation.svg';
import React, { useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useOpenAppOrStorePage } from '../../../common/hooks/use-open-app-or-store-page';
import { Contact } from './components/Contact/Contact';
import { ConversationHeader } from './components/ConversationHeader/ConversationHeader';
import { Conversations } from './components/Conversations/Conversations';
import { ArchivedMessages } from './components/Messages/ArchivedMessages';
import { Messages } from './components/Messages/Messages';
import { MessageSearchMatches } from './components/Messages/MessageSearchMatches';
import { MessageSender } from './components/MessageSender/MessageSender';
import { ContactContext, EmptySelectedContact } from './contactMachine';
import { CurrentPageNameContext, PersonaContext } from './HelperContexts';
import { useCurrentPageName } from './hooks/use-current-page-name';
import { usePersonas } from './hooks/use-personas';
import { LiveChatContext } from './LiveChatContext';
import './LiveChatPanel.css';

export enum Panes {
  'conversations',
  'messages',
  'contact',
}

export type Conversation = {
  lastMessageId: number;
  contactName: string;
  lastMessage: string;
  lastMessageAt: number;
};

export type ParsedLivechatMessageModel = LivechatMessageModel & {
  textToDisplay: string | undefined;
  buttonsTexts?: string[];
};

export const LiveChatBase = () => {
  const { isMobile } = useResolutionInfo();
  const modalActions = useModalActions();

  const [activePane, setActivePane] = useState(Panes.conversations);
  const [collapsedContactView, setCollapsedContactView] = useState(true);

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  useOpenAppOrStorePage();

  const [lcMachineState] = useActor(lcService);

  const { remoteUserId, searchResultsMessages, remoteFbPageId } =
    lcMachineState.context || {};
  const { currentPersona, fetchPersona } = usePersonas(remoteFbPageId || '');

  const getCurrentPageName = useCurrentPageName();

  const contextValue = useMemo(() => {
    return {
      getCurrentPageName,
    };
  }, [getCurrentPageName]);

  const personaContextValue = useMemo(() => {
    return {
      currentPersona,
      fetchPersona,
    };
  }, [currentPersona, remoteFbPageId, fetchPersona]);

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const isFetchingSearchResultsMessages = lcMachineState.matches({
    mainLogic: {
      active: {
        searchResultsMessagesFetching: 'fetching',
      },
    },
  });

  const selectedContactMachine = remoteUserId
    ? lcMachineState?.context?.contactsRefs[remoteUserId]
    : undefined;

  const renderConversations = () => (
    <Conversations setActivePane={setActivePane} />
  );

  const showSearchMessages =
    searchResultsMessages.length > 0 || isFetchingSearchResultsMessages;

  const renderMessages = () => {
    if (!selectedContactMachine) {
      return null;
    }

    const isFetchingArchivedMessages = selectedContactMachine?.state.matches({
      archivedMessages: 'fetching',
    });

    const archivedMessages =
      (selectedContactMachine?.state.context as ContactContext)
        .archivedMessages || [];

    const showArchivedMessages =
      !!archivedMessages.length || isFetchingArchivedMessages;

    return (
      <CurrentPageNameContext.Provider value={contextValue}>
        <ConversationHeader setActivePane={setActivePane} />
        {showSearchMessages ? (
          <MessageSearchMatches />
        ) : showArchivedMessages ? (
          <ArchivedMessages />
        ) : (
          <Messages />
        )}
      </CurrentPageNameContext.Provider>
    );
  };

  const renderContact = () => (
    <Contact
      // https://bit.ly/2JQF6Dc
      key={`contact_${remoteUserId}`}
      collapsedContactView={collapsedContactView}
      setCollapsedContactView={setCollapsedContactView}
    />
  );

  const renderChatWindow = () => {
    return selectedContact?.id || remoteUserId ? (
      <PersonaContext.Provider value={personaContextValue}>
        <div className="live-chat--messages-container">
          {renderMessages()}
          <MessageSender />
        </div>
      </PersonaContext.Provider>
    ) : (
      <div className="live-chat--messages-container_empty">
        <img
          src={conversation}
          className="live-chat--messages-container-image"
        />
        <Heading
          className="live-chat--messages-container-text"
          size={6}
          text={'Please select a conversation to start messaging'}
        />
      </div>
    );
  };

  const toRender = !lcMachineState ? null : isMobile ? (
    <PersonaContext.Provider value={personaContextValue}>
      <div className="live-chat live-chat--mobile">
        {activePane === Panes.conversations && renderConversations()}
        {activePane === Panes.messages && renderMessages()}
        {activePane === Panes.contact && renderContact()}
      </div>
    </PersonaContext.Provider>
  ) : (
    <div className="live-chat">
      {renderConversations()}
      {renderChatWindow()}
      {renderContact()}
    </div>
  );

  return toRender;
};

// Note: needed to guard against context not being ready
export const LiveChat = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef?.current,
  );

  return lcService ? <LiveChatBase /> : null;
};
